import React, { useState, useMemo } from 'react';
import './DynamicCalendar.css';

/**
 * DynamicCalendar - A reusable calendar component with weekly, monthly, and yearly views
 * 
 * @param {Array} events - Array of event objects with structure: { id, title, date, status, type, ...customFields }
 * @param {Function} onEventClick - Callback when an event is clicked: (event) => void
 * @param {String} initialView - Initial view mode: 'week' | 'month' | 'year' (default: 'month')
 * @param {Date} initialDate - Initial date to display (default: current date)
 * @param {Function} onDateChange - Callback when date/view changes: (date, view) => void
 * @param {Function} eventRenderer - Custom render function for events: (event) => ReactNode
 * @param {Object} config - Configuration object for customization
 * @param {Boolean} isLoading - Whether data is currently loading
 * @param {Error} error - Error object if fetch failed
 * @param {Function} onRetry - Callback to retry failed request
 */
const DynamicCalendar = ({
    events = [],
    onEventClick = () => { },
    initialView = 'month',
    initialDate = new Date(),
    onDateChange = () => { },
    eventRenderer = null,
    config = {},
    isLoading = false,
    error = null,
    onRetry = () => { },
    loadingComponent = null,  // custom loader node injected from parent
    headerLegend = null,      // custom legend node injected below title
    statusConfig = null,      // { approved: '#10b981', ... }
    entityLabel = 'task',     // default label for tooltips
}) => {
    const [currentDate, setCurrentDate] = useState(initialDate || new Date());
    const [calendarView, setCalendarView] = useState(initialView); // 'day', 'week', 'month', 'year'

    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    // Navigation Functions
    const navigatePrevious = () => {
        const newDate = new Date(currentDate);
        if (calendarView === 'day') {
            newDate.setDate(newDate.getDate() - 1);
        } else if (calendarView === 'week') {
            newDate.setDate(newDate.getDate() - 7);
        } else if (calendarView === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setFullYear(newDate.getFullYear() - 1);
        }
        setCurrentDate(newDate);
        onDateChange(newDate, calendarView);
    };

    const navigateNext = () => {
        const newDate = new Date(currentDate);
        if (calendarView === 'day') {
            newDate.setDate(newDate.getDate() + 1);
        } else if (calendarView === 'week') {
            newDate.setDate(newDate.getDate() + 7);
        } else if (calendarView === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
        } else {
            newDate.setFullYear(newDate.getFullYear() + 1);
        }
        setCurrentDate(newDate);
        onDateChange(newDate, calendarView);
    };

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        onDateChange(today, calendarView);
    };

    const changeView = (view) => {
        setCalendarView(view);
        onDateChange(currentDate, view);
    };

    // Helper: Format date string for comparison
    const formatDateKey = (date) => {
        if (!date) return 'invalid';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'invalid';
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    // Optimized: Group events by date string
    const groupedEvents = useMemo(() => {
        const groups = {};
        events.forEach(event => {
            if (!event.date) return;
            const key = formatDateKey(event.date);
            if (!groups[key]) groups[key] = [];
            groups[key].push(event);
        });
        return groups;
    }, [events]);

    // Helper: Get events for a specific date
    const getEventsForDate = (date) => {
        const key = formatDateKey(date);
        return groupedEvents[key] || [];
    };

    // Default event renderer
    const renderEvent = (event) => {
        if (eventRenderer) return (
            <div 
                key={event.id} 
                className="custom-event-wrapper" 
                style={{ cursor: 'pointer', width: '100%' }}
                onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                }}
            >
                {eventRenderer(event, calendarView)}
            </div>
        );

        return (
            <div
                key={event.id}
                className={`cal-event ${event.status?.toLowerCase() || ''}`}
                style={statusConfig && event.status && statusConfig[event.status.toLowerCase()] ? { backgroundColor: statusConfig[event.status.toLowerCase()] } : {}}
                onClick={(e) => {
                    e.stopPropagation();
                    onEventClick(event);
                }}
                title={event.title}
            >
                {event.title}
            </div>
        );
    };

    // ========== MONTHLY VIEW ==========
    const renderMonthView = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Weekday headers
        weekdays.forEach(day => {
            days.push(<div key={`header-${day}`} className="cal-weekday">{day}</div>);
        });

        // Empty cells for days before month starts
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="cal-day-cell empty"></div>);
        }

        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayEvents = getEventsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();

            days.push(
                <div
                    key={day}
                    className={`cal-day-cell ${isToday ? 'today' : ''}`}
                >
                    <span className="cal-date-num">{day}</span>
                    <div className="cal-event-container">
                        {dayEvents.map(event => renderEvent(event))}
                    </div>
                </div>
            );
        }

        return <div className="calendar-grid monthly">{days}</div>;
    };

    // ========== WEEKLY VIEW ==========
    const renderWeekView = () => {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(startOfWeek);
            date.setDate(startOfWeek.getDate() + i);
            const dayEvents = getEventsForDate(date);
            const isToday = date.toDateString() === new Date().toDateString();

            weekDays.push(
                <div key={i} className={`week-day-column ${isToday ? 'today' : ''}`}>
                    <div className="week-day-header">
                        <div className="week-day-name">{weekdays[i]}</div>
                        <div className="week-day-date">{date.getDate()}</div>
                    </div>
                    <div className="week-events-container">
                        {dayEvents.length > 0 ? (
                            dayEvents.map(event => renderEvent(event))
                        ) : (
                            <div className="no-events">No {entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)}</div>
                        )}
                    </div>
                </div>
            );
        }

        return <div className="calendar-grid weekly">{weekDays}</div>;
    };
    
    // ========== DAILY VIEW ==========
    const renderDayView = () => {
        const date = new Date(currentDate);
        const dayEvents = getEventsForDate(date);
        const isToday = date.toDateString() === new Date().toDateString();

        return (
            <div className="calendar-grid daily">
                <div className={`day-column full-width ${isToday ? 'today' : ''}`}>
                    <div className="day-header-standalone">
                        <div className="day-name-lg">{weekdays[date.getDay()]}</div>
                        <div className="day-date-lg">{monthNames[date.getMonth()]} {date.getDate()}, {date.getFullYear()}</div>
                    </div>
                    <div className="day-events-container-standalone">
                        {dayEvents.length > 0 ? (
                            <div className="day-events-list">
                                {dayEvents.map(event => renderEvent(event))}
                            </div>
                        ) : (
                            <div className="no-events-standalone">
                                <div className="no-events-icon">📅</div>
                                <div className="no-events-text">No {entityLabel} scheduled for this day</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ========== YEARLY VIEW ==========
    const renderYearView = () => {
        const year = currentDate.getFullYear();
        const months = [];

        for (let monthIdx = 0; monthIdx < 12; monthIdx++) {
            const firstDay = new Date(year, monthIdx, 1).getDay();
            const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
            const miniDays = [];

            // Mini weekday headers
            weekdays.forEach(day => {
                miniDays.push(<div key={`mini-header-${monthIdx}-${day}`} className="mini-weekday">{day[0]}</div>);
            });

            // Empty cells
            for (let i = 0; i < firstDay; i++) {
                miniDays.push(<div key={`mini-empty-${monthIdx}-${i}`} className="mini-day-cell empty"></div>);
            }

            // Days
            for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(year, monthIdx, day);
                const dayEvents = getEventsForDate(date);
                const hasEvents = dayEvents.length > 0;
                const isToday = date.toDateString() === new Date().toDateString();

                // Calculate status distribution for multi-color "slices"
                let backgroundStyle = {};

                // Use provided statusConfig or default to task statuses
                const colors = statusConfig || {
                    overdue: '#ef4444',
                    upcoming: '#f97316', // Orange
                    completed: '#10b981',
                    open: '#2563eb'
                };

                let statusCounts = {};
                Object.keys(colors).forEach(key => statusCounts[key] = 0);

                if (hasEvents) {
                    dayEvents.forEach(ev => {
                        const s = (ev.computedStatus || ev.status || (statusConfig ? Object.keys(statusConfig)[0] : 'open')).toLowerCase();
                        if (statusCounts.hasOwnProperty(s)) statusCounts[s]++;
                        else if (s === 'closed' && statusCounts.hasOwnProperty('completed')) statusCounts.completed++;
                        else if (statusConfig) {
                            // If custom config, try to find a match or use first key
                            const firstKey = Object.keys(statusConfig)[0];
                            if (statusCounts.hasOwnProperty(firstKey)) statusCounts[firstKey]++;
                        } else {
                            statusCounts.open++;
                        }
                    });

                    const total = dayEvents.length;
                    const activeStatuses = Object.keys(statusCounts).filter(s => statusCounts[s] > 0);

                    if (activeStatuses.length === 1) {
                        backgroundStyle = { backgroundColor: colors[activeStatuses[0]] };
                    } else if (activeStatuses.length > 1) {
                        let currentPos = 0;
                        const segments = [];
                        activeStatuses.forEach((s, idx) => {
                            const per = (statusCounts[s] / total) * 100;
                            const nextPos = idx === activeStatuses.length - 1 ? 100 : currentPos + per;
                            segments.push(`${colors[s]} ${currentPos}% ${nextPos}%`);
                            currentPos = nextPos;
                        });
                        backgroundStyle = { background: `conic-gradient(${segments.join(', ')})` };
                    }
                }

                // Generate tooltip text
                let tooltipText = '';
                if (hasEvents) {
                    if (config.yearlyTooltipRenderer) {
                        tooltipText = config.yearlyTooltipRenderer(dayEvents, statusCounts);
                    } else {
                        const label = entityLabel || 'task';
                        tooltipText = `${dayEvents.length} ${label}(s):\n` +
                            Object.keys(statusCounts)
                                .filter(s => statusCounts[s] > 0)
                                .map(s => `- ${s.charAt(0).toUpperCase() + s.slice(1)}: ${statusCounts[s]}`)
                                .join('\n');
                    }
                }

                miniDays.push(
                    <div
                        key={`mini-day-${monthIdx}-${day}`}
                        className={`mini-day-cell ${hasEvents ? 'has-events' : ''} ${isToday ? 'today' : ''}`}
                        style={backgroundStyle}
                        title={tooltipText}
                        onClick={() => {
                            setCurrentDate(date);
                            setCalendarView('month');
                            onDateChange(date, 'month');
                        }}
                    >
                        {day}
                    </div>
                );
            }

            months.push(
                <div key={monthIdx} className="year-month-container">
                    <div className="year-month-name">{monthNames[monthIdx]}</div>
                    <div className="mini-calendar-grid">{miniDays}</div>
                </div>
            );
        }

        return <div className="calendar-grid yearly">{months}</div>;
    };

    // Get header title based on view
    const getHeaderTitle = () => {
        const safeDate = currentDate || new Date();
        const year = safeDate.getFullYear();
        const month = monthNames[safeDate.getMonth()];

        if (calendarView === 'day') {
            return `${weekdays[safeDate.getDay()]}, ${month} ${safeDate.getDate()}, ${year}`;
        } else if (calendarView === 'week') {
            const startOfWeek = new Date(currentDate);
            startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            return `${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getDate()} - ${startOfWeek.getMonth() !== endOfWeek.getMonth() ? monthNames[endOfWeek.getMonth()] + ' ' : ''
                }${endOfWeek.getDate()}, ${year}`;
        } else if (calendarView === 'month') {
            return `${month} ${year}`;
        } else {
            return `${year}`;
        }
    };

    return (
        <div className="dynamic-calendar-container">
            {/* Navigation Header */}
            <div className="calendar-header">
                <div className="calendar-header-left">
                    <div className="calendar-title-section">
                        <h3 className="calendar-title">{getHeaderTitle()}</h3>
                    </div>

                    <div className="calendar-nav-buttons">
                        <button className="cal-nav-btn" onClick={navigatePrevious} title="Previous">‹</button>
                        <button className="cal-nav-btn today-btn" onClick={goToToday}>Today</button>
                        <button className="cal-nav-btn" onClick={navigateNext} title="Next">›</button>
                    </div>

                    {headerLegend && <div className="calendar-header-legend-row">{headerLegend}</div>}
                </div>

                <div className="calendar-view-switcher">
                    <button
                        className={`view-btn ${calendarView === 'day' ? 'active' : ''}`}
                        onClick={() => changeView('day')}
                    >
                        Day
                    </button>
                    <button
                        className={`view-btn ${calendarView === 'week' ? 'active' : ''}`}
                        onClick={() => changeView('week')}
                    >
                        Week
                    </button>
                    <button
                        className={`view-btn ${calendarView === 'month' ? 'active' : ''}`}
                        onClick={() => changeView('month')}
                    >
                        Month
                    </button>
                    <button
                        className={`view-btn ${calendarView === 'year' ? 'active' : ''}`}
                        onClick={() => changeView('year')}
                    >
                        Year
                    </button>
                </div>
            </div>

            {/* Calendar Content */}
            <div className="calendar-content">
                {isLoading ? (
                    loadingComponent ? (
                        <div className="calendar-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                            {loadingComponent}
                        </div>
                    ) : (
                        <div className="calendar-loading" style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '400px',
                            fontSize: '18px',
                            color: '#666'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                                <div>Loading events...</div>
                            </div>
                        </div>
                    )
                ) : error ? (
                    <div className="calendar-error" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '400px',
                        fontSize: '16px',
                        color: '#e74c3c'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                            <div style={{ marginBottom: '16px' }}>Error loading events</div>
                            <button
                                onClick={onRetry}
                                style={{
                                    padding: '8px 16px',
                                    background: '#3498db',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {calendarView === 'day' && renderDayView()}
                        {calendarView === 'month' && renderMonthView()}
                        {calendarView === 'week' && renderWeekView()}
                        {calendarView === 'year' && renderYearView()}
                    </>
                )}
            </div>
        </div>
    );
};

export default DynamicCalendar;
